import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { contadorMisEmpresas, contadorSolicitar, contadorMisReferidos } from '../services/api';

const fmt = (n) => '$' + Number(n || 0).toLocaleString('es-CO');
const fdate = (d) => d ? new Date(d).toLocaleDateString('es-CO') : '—';

const EST = {
  aprobado:  { l: 'Aprobado',  c: '#10b981', bg: '#022c22' },
  pendiente: { l: 'Pendiente', c: '#fbbf24', bg: '#422006' },
  rechazado: { l: 'Rechazado', c: '#fca5a5', bg: '#450a0a' },
  revocado:  { l: 'Revocado',  c: '#fca5a5', bg: '#450a0a' },
};

export default function ContadorPanel() {
  const { user, setActingTenant, logout } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [refs, setRefs] = useState([]);
  const [nit, setNit] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const cargar = () => {
    contadorMisEmpresas().then(r => setEmpresas(r.data)).catch(() => {});
    contadorMisReferidos().then(r => setRefs(r.data)).catch(() => {});
  };
  useEffect(cargar, []);

  const entrar = (e) => { setActingTenant(e.tenant_id, e.empresa); navigate('/dashboard'); };

  const solicitar = async () => {
    if (!nit.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await contadorSolicitar({ nit });
      setMsg({ ok: true, text: `✅ ${data.mensaje} (${data.empresa})` });
      setNit(''); cargar();
    } catch (e) { setMsg({ ok: false, text: '❌ ' + (e.response?.data?.error || 'No se pudo enviar') }); }
    finally { setBusy(false); }
  };

  const copiarRef = () => { navigator.clipboard?.writeText(user?.codigo_referido || ''); setMsg({ ok: true, text: 'Código de referido copiado' }); };

  return (
    <div style={s.wrap}>
      <div style={s.top}>
        <div style={s.brand}>Snel<span style={{ color: '#3b82f6' }}>ERP</span> <span style={s.rolBadge}>Contador</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>{user?.nombre}</span>
          <button style={s.salir} onClick={logout}>Salir</button>
        </div>
      </div>

      <div style={s.body}>
        <h1 style={s.h1}>Tus empresas</h1>
        <p style={s.sub}>Selecciona una empresa para revisar y llevar su contabilidad.</p>

        {msg && <div style={{ ...s.msg, background: msg.ok ? '#064e3b' : '#450a0a', color: msg.ok ? '#6ee7b7' : '#fca5a5' }}>{msg.text}</div>}

        <div style={s.grid}>
          {empresas.length === 0 && <p style={{ color: '#64748b' }}>Aún no tienes empresas. Solicita acceso más abajo con el NIT de tu cliente.</p>}
          {empresas.map(e => {
            const est = EST[e.estado] || EST.pendiente;
            return (
              <div key={e.id} style={s.card}>
                <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: 15 }}>{e.empresa}</div>
                <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>NIT {e.nit || '—'}</div>
                <span style={{ ...s.badge, background: est.bg, color: est.c }}>{est.l}</span>
                {e.estado === 'aprobado'
                  ? <button style={s.btnEntrar} onClick={() => entrar(e)}>Entrar →</button>
                  : <div style={{ color: '#64748b', fontSize: 12, marginTop: 10 }}>{e.estado === 'pendiente' ? 'Esperando aprobación de la empresa.' : 'Acceso no disponible.'}</div>}
              </div>
            );
          })}
        </div>

        <div style={s.solicitar}>
          <h2 style={s.h2}>Solicitar acceso a una empresa</h2>
          <p style={s.sub}>Escribe el NIT (sin dígito de verificación). La empresa recibirá tu solicitud y deberá aprobarla.</p>
          <div style={{ display: 'flex', gap: 8, maxWidth: 420 }}>
            <input style={s.input} value={nit} onChange={e => setNit(e.target.value)} placeholder="Ej. 901463973" onKeyDown={e => e.key === 'Enter' && solicitar()} />
            <button style={s.btnPri} onClick={solicitar} disabled={busy}>{busy ? 'Enviando…' : 'Solicitar'}</button>
          </div>
        </div>

        <div style={s.solicitar}>
          <h2 style={s.h2}>Tu código de referido</h2>
          <p style={s.sub}>Comparte este código con las empresas que registres. Las que se inscriban con él quedan asociadas a ti.</p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code style={s.code}>{user?.codigo_referido || '—'}</code>
            <button style={s.btnSec} onClick={copiarRef}>Copiar</button>
          </div>
          {refs.length > 0 && (
            <table style={s.table}>
              <thead><tr>{['Empresa', 'Registrada', 'Estado', 'Vence'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {refs.map(r => (
                  <tr key={r.id}>
                    <td style={s.td}>{r.nombre}</td>
                    <td style={s.td}>{fdate(r.created_at)}</td>
                    <td style={s.td}>{!r.activo ? 'Suspendida' : r.es_prueba ? 'En prueba' : 'Activa'}</td>
                    <td style={s.td}>{fdate(r.fecha_vencimiento)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  wrap: { minHeight: '100vh', background: '#0f172a', color: '#f1f5f9', fontFamily: 'system-ui,sans-serif' },
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1e293b', padding: '14px 22px', borderBottom: '1px solid #334155' },
  brand: { fontWeight: 800, fontSize: 20 },
  rolBadge: { fontSize: 11, background: '#1e3a5f', color: '#7dd3fc', border: '1px solid #3b82f6', borderRadius: 6, padding: '2px 8px', fontWeight: 600, marginLeft: 6, verticalAlign: 'middle' },
  salir: { background: '#334155', color: '#cbd5e1', border: '1px solid #475569', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' },
  body: { maxWidth: 980, margin: '0 auto', padding: '26px 20px' },
  h1: { fontSize: 24, fontWeight: 700, margin: '0 0 2px' },
  h2: { fontSize: 16, fontWeight: 600, margin: '0 0 4px' },
  sub: { color: '#64748b', fontSize: 13, margin: '0 0 14px' },
  msg: { padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 14, marginBottom: 26 },
  card: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 18 },
  badge: { fontSize: 11, borderRadius: 6, padding: '2px 8px', fontWeight: 600 },
  btnEntrar: { display: 'block', width: '100%', marginTop: 12, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  solicitar: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 20, marginBottom: 18 },
  input: { flex: 1, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '9px 12px', color: '#f1f5f9', fontSize: 14 },
  btnPri: { background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '0 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSec: { background: '#334155', color: '#cbd5e1', border: '1px solid #475569', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  code: { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '9px 14px', color: '#93c5fd', fontSize: 15, fontWeight: 700, letterSpacing: 1 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 16, fontSize: 13 },
  th: { textAlign: 'left', padding: '8px', color: '#64748b', fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #334155' },
  td: { padding: '8px', color: '#cbd5e1', borderBottom: '1px solid #1e293b' },
};
